if (!customElements.get('pickup-availability')) {
  class PickupAvailability extends HTMLElement {
    constructor() {
      super();

      this.isLocalProduct = this.dataset.localProduct === 'true';
      if (!this.isLocalProduct && !this.hasAttribute('available')) return;

      this.refreshHandler = this.handleRefreshBtnClick.bind(this);
      this.unavailableHtml = this.querySelector('template').content.firstElementChild.cloneNode(true);
      PickupAvailability.instanceCount = (PickupAvailability.instanceCount || 0) + 1;
      this.instanceId = `PickupAvailability-${PickupAvailability.instanceCount}`;
      this.requestSequence = 0;
      this.abortController = null;
      this.localModal = null;

      this.getAvailability(this.dataset.variantId);
    }

    disconnectedCallback() {
      if (this.abortController) this.abortController.abort();
      this.removeLocalModal();
    }

    /**
     * Handles click events on retry buttons.
     */
    handleRefreshBtnClick() {
      this.getAvailability(this.dataset.variantId);
    }

    /**
     * Fetches pickup markup and, for local products, exact selected-variant store inventory.
     * @param {string} variantId - Current variant id.
     */
    async getAvailability(variantId) {
      if (!variantId) {
        this.renderUnavailableMsg();
        return;
      }

      this.dataset.variantId = variantId;
      this.requestSequence += 1;
      const requestSequence = this.requestSequence;

      if (this.abortController) this.abortController.abort();
      this.abortController = new AbortController();
      const { signal } = this.abortController;

      let { rootUrl } = this.dataset;
      if (!rootUrl.endsWith('/')) rootUrl = `${rootUrl}/`;

      try {
        const requests = [this.fetchSection(rootUrl, variantId, signal)];
        if (this.isLocalProduct) requests.push(this.fetchLocalStoreAvailability(variantId, signal));

        const results = await Promise.allSettled(requests);
        if (requestSequence !== this.requestSequence) return;

        if (results[0].status !== 'fulfilled') throw results[0].reason;

        if (this.isLocalProduct) {
          this.renderLocalShell(results[0].value);

          if (results[1].status === 'fulfilled') {
            this.hydrateLocalAvailability(results[1].value);
          } else {
            this.renderLocalError();
          }
          return;
        }

        this.renderPickupInfo(results[0].value);
      } catch (error) {
        if (error.name === 'AbortError' || requestSequence !== this.requestSequence) return;
        this.renderUnavailableMsg();
      }
    }

    /**
     * Fetches Shopify's variant-specific pickup section.
     * @param {string} rootUrl - Shop root URL.
     * @param {string} variantId - Variant numeric ID.
     * @param {AbortSignal} signal - Request cancellation signal.
     * @returns {Promise<Element>} Parsed section element.
     */
    async fetchSection(rootUrl, variantId, signal) {
      const response = await fetch(`${rootUrl}variants/${variantId}/?section_id=pickup-availability`, { signal });
      if (!response.ok) throw new Error(`Pickup section request failed: ${response.status}`);

      const tmpl = document.createElement('template');
      tmpl.innerHTML = await response.text();
      const section = tmpl.content.querySelector('.shopify-section');
      if (!section) throw new Error('Pickup section markup was missing');
      return section;
    }

    /**
     * Fetches physical quantity and Shopify pickup promises for the selected variant.
     * @param {string} variantId - Variant numeric ID.
     * @param {AbortSignal} signal - Request cancellation signal.
     * @returns {Promise<Array>} Store availability nodes.
     */
    async fetchLocalStoreAvailability(variantId, signal) {
      const apiVersion = this.dataset.storefrontApiVersion;
      const storefrontToken = this.dataset.storefrontToken;
      if (!apiVersion || !storefrontToken) throw new Error('Store availability configuration is missing');

      const query = `
        query VariantStoreAvailability($id: ID!) {
          node(id: $id) {
            ... on ProductVariant {
              id
              storeAvailability(first: 20) {
                nodes {
                  available
                  quantityAvailable
                  pickUpTime
                  location {
                    id
                    name
                  }
                }
              }
            }
          }
        }
      `;

      const response = await fetch(`/api/${apiVersion}/graphql.json`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Shopify-Storefront-Access-Token': storefrontToken,
        },
        body: JSON.stringify({
          query,
          variables: { id: `gid://shopify/ProductVariant/${variantId}` },
        }),
        signal,
      });

      if (!response.ok) throw new Error(`Store availability request failed: ${response.status}`);

      const payload = await response.json();
      if (payload.errors?.length || !payload.data?.node) throw new Error('Store availability data was unavailable');

      return payload.data.node.storeAvailability?.nodes || [];
    }

    /**
     * Inserts the local panel and its matching modal.
     * @param {Element} sectionHtml - Fetched pickup section HTML.
     */
    renderLocalShell(sectionHtml) {
      const summary = sectionHtml.querySelector('[data-local-fulfillment-summary]');
      const modal = sectionHtml.querySelector('[data-local-fulfillment-modal]');
      if (!summary || !modal) throw new Error('Local fulfillment markup was missing');

      const modalId = `${this.instanceId}-modal`;
      const modalHeading = modal.querySelector('[id$="-heading"]');
      const modalOpener = summary.querySelector('modal-opener');
      const modalButton = modalOpener?.querySelector('button');
      modal.id = modalId;
      if (modalHeading) {
        modalHeading.id = `${modalId}-heading`;
        modal.setAttribute('aria-labelledby', modalHeading.id);
      }
      modal.querySelectorAll('[data-local-store-card]').forEach((storeCard, index) => {
        const storeHeading = storeCard.querySelector('.ao-local-store-card__title');
        if (!storeHeading) return;
        storeHeading.id = `${modalId}-store-${index + 1}`;
        storeCard.setAttribute('aria-labelledby', storeHeading.id);
      });
      if (modalOpener) modalOpener.dataset.modal = modalId;
      if (modalButton) modalButton.setAttribute('aria-controls', modalId);

      this.removeLocalModal();
      this.setAttribute('available', '');
      this.innerHTML = summary.outerHTML;
      this.localModal = modal;
      document.body.appendChild(this.localModal);
      const renderedOpenerButton = this.querySelector('.ao-local-fulfillment__details-button');
      if (renderedOpenerButton) {
        renderedOpenerButton.addEventListener('click', () => {
          const modalWindow = this.localModal?.querySelector('.ao-local-modal__window');
          if (modalWindow) modalWindow.scrollTop = 0;
        }, { capture: true });
      }
      this.bindRefreshButton();
    }

    /**
     * Applies the exact physical/transfer state to the panel and modal.
     * @param {Array} availabilityNodes - Storefront StoreAvailability nodes.
     */
    hydrateLocalAvailability(availabilityNodes) {
      const summary = this.querySelector('[data-local-fulfillment-summary]');
      if (!summary || !this.localModal) return;

      const knownLocationIds = [
        'gid://shopify/Location/72371896626',
        'gid://shopify/Location/113344020786',
      ];
      const availabilityByLocation = new Map(
        availabilityNodes
          .filter((node) => knownLocationIds.includes(node.location?.id))
          .map((node) => [node.location.id, node]),
      );

      const states = new Map();
      knownLocationIds.forEach((locationId) => {
        const availability = availabilityByLocation.get(locationId);
        const quantity = Number(availability?.quantityAvailable);
        let state = 'unavailable';

        if (Number.isFinite(quantity) && quantity > 0) {
          state = 'in-stock';
        } else if (availability?.available) {
          state = 'transfer';
        }

        states.set(locationId, { state, availability });
      });

      const actionableLocations = knownLocationIds.filter((locationId) => states.get(locationId).state !== 'unavailable');

      knownLocationIds.forEach((locationId) => {
        const { state, availability } = states.get(locationId);
        const shouldHide = state === 'unavailable' && actionableLocations.length > 0;
        const statusLabel = this.getStatusLabel(summary, state);
        const pickupTime = availability?.pickUpTime || '';
        const inlineStatus = pickupTime && state !== 'unavailable' ? `${statusLabel} · ${pickupTime}` : statusLabel;

        const summaryLocation = this.querySelector(`[data-local-summary-location][data-location-id="${locationId}"]`);
        if (summaryLocation) {
          summaryLocation.hidden = shouldHide;
          summaryLocation.dataset.state = state;
          const status = summaryLocation.querySelector('[data-local-status]');
          if (status) status.textContent = inlineStatus;
        }

        const storeCard = this.localModal.querySelector(`[data-local-store-card][data-location-id="${locationId}"]`);
        if (storeCard) {
          storeCard.hidden = shouldHide;
          storeCard.dataset.state = state;

          const status = storeCard.querySelector('[data-local-status]');
          const badge = storeCard.querySelector('[data-local-badge]');
          const eta = storeCard.querySelector('[data-local-eta]');
          const actions = storeCard.querySelector('.ao-local-store-card__actions');

          if (status) status.textContent = statusLabel;
          if (badge) badge.textContent = statusLabel;
          if (eta) {
            eta.textContent = pickupTime;
            eta.hidden = !pickupTime || state === 'unavailable';
          }
          if (actions) actions.hidden = state === 'unavailable';
        }
      });

      const locationList = summary.querySelector('.ao-local-fulfillment__locations');
      if (locationList) locationList.setAttribute('aria-busy', 'false');

      this.updatePhoneLinks(actionableLocations.length ? actionableLocations : knownLocationIds);
      const retryButton = this.querySelector('.js-refresh');
      if (retryButton) retryButton.hidden = true;
    }

    /**
     * Converts a state key to copy stored on the rendered panel.
     * @param {Element} summary - Local summary element.
     * @param {string} state - in-stock, transfer, unavailable, or error.
     * @returns {string} User-facing status label.
     */
    getStatusLabel(summary, state) {
      if (state === 'in-stock') return summary.dataset.labelInStock;
      if (state === 'transfer') return summary.dataset.labelTransfer;
      if (state === 'error') return summary.dataset.labelLoadError;
      return summary.dataset.labelUnavailable;
    }

    /**
     * Shows only the phone numbers relevant to the current selected variant.
     * @param {Array<string>} visibleLocationIds - Location IDs whose phone should display.
     */
    updatePhoneLinks(visibleLocationIds) {
      const phoneLocations = [...this.querySelectorAll('[data-phone-location]')];
      phoneLocations.forEach((phoneLocation) => {
        phoneLocation.hidden = !visibleLocationIds.includes(phoneLocation.dataset.phoneLocation);
      });

      const separator = this.querySelector('.ao-local-fulfillment__phone-separator');
      if (separator) separator.hidden = phoneLocations.filter((phoneLocation) => !phoneLocation.hidden).length < 2;
    }

    /**
     * Keeps the local panel useful when inventory data cannot be loaded.
     */
    renderLocalError() {
      const summary = this.querySelector('[data-local-fulfillment-summary]');
      if (!summary) {
        this.renderUnavailableMsg();
        return;
      }

      const errorLabel = this.getStatusLabel(summary, 'error');
      this.querySelectorAll('[data-local-summary-location]').forEach((location) => {
        location.hidden = false;
        location.dataset.state = 'error';
        const status = location.querySelector('[data-local-status]');
        if (status) status.textContent = errorLabel;
      });

      if (this.localModal) {
        this.localModal.querySelectorAll('[data-local-store-card]').forEach((storeCard) => {
          storeCard.hidden = false;
          storeCard.dataset.state = 'error';
          const status = storeCard.querySelector('[data-local-status]');
          const badge = storeCard.querySelector('[data-local-badge]');
          const eta = storeCard.querySelector('[data-local-eta]');
          if (status) status.textContent = errorLabel;
          if (badge) badge.textContent = errorLabel;
          if (eta) eta.hidden = true;
        });
      }

      const locationList = summary.querySelector('.ao-local-fulfillment__locations');
      if (locationList) locationList.setAttribute('aria-busy', 'false');
      this.updatePhoneLinks([
        'gid://shopify/Location/72371896626',
        'gid://shopify/Location/113344020786',
      ]);

      const retryButton = this.querySelector('.js-refresh');
      if (retryButton) retryButton.hidden = false;
      this.bindRefreshButton();
    }

    /**
     * Removes only the modal owned by this component instance.
     */
    removeLocalModal() {
      if (!this.localModal) return;
      if (this.localModal.hasAttribute('open') && typeof this.localModal.close === 'function') this.localModal.close();
      this.localModal.remove();
      this.localModal = null;
    }

    /**
     * Renders the theme's native pickup experience for non-local products.
     * @param {Element} sectionHtml - Fetched section HTML.
     */
    renderPickupInfo(sectionHtml) {
      const drawer = document.querySelector('.js-pickup-drawer');
      if (drawer) drawer.remove();

      if (!sectionHtml.querySelector('.pickup-status')) {
        this.innerHTML = '';
        this.removeAttribute('available');
        return;
      }

      this.setAttribute('available', '');
      this.innerHTML = sectionHtml.querySelector('.pickup-status').outerHTML;
      document.body.appendChild(sectionHtml.querySelector('.js-pickup-drawer'));

      const showDetailsBtn = this.querySelector('.js-show-pickup-info');
      if (showDetailsBtn) {
        showDetailsBtn.addEventListener('click', () => {
          document.querySelector('.js-pickup-drawer').open(showDetailsBtn);
        });
      }
    }

    /**
     * Binds the currently rendered retry button once.
     */
    bindRefreshButton() {
      const refreshButton = this.querySelector('.js-refresh');
      if (!refreshButton) return;
      refreshButton.removeEventListener('click', this.refreshHandler);
      refreshButton.addEventListener('click', this.refreshHandler);
    }

    /**
     * Renders the safe retry state when section loading fails.
     */
    renderUnavailableMsg() {
      this.removeLocalModal();
      this.innerHTML = '';
      this.appendChild(this.unavailableHtml.cloneNode(true));
      this.bindRefreshButton();
    }

    /**
     * Clears stale store data when the selected options do not resolve to a variant.
     */
    renderNoSelectionMsg() {
      this.removeLocalModal();
      this.innerHTML = '';
      this.appendChild(this.unavailableHtml.cloneNode(true));
      const errorText = this.querySelector('[data-local-error-text]');
      const retryButton = this.querySelector('.js-refresh');
      if (errorText) errorText.textContent = 'Choose available options to see store availability.';
      if (retryButton) retryButton.hidden = true;
    }
  }

  customElements.define('pickup-availability', PickupAvailability);
}
